import { useEffect, useMemo } from 'react'
import type { CardIdQuery } from '../utils/cardIdQuery'
import {
    applyCardSharePayloadToStore,
    decodeCardSharePayload,
    type CardSharePayload,
} from '../utils/cardSharePayload'

/**
 * Decodes the `?cID` base64url tuple into a {@link CardSharePayload}.
 *
 * Applies the decoded chameleon customization to the global store (so the 3D
 * scene matches the shared card) and returns the payload so callers (e.g. the
 * card landing) can drive the collectible-card visuals. Returns `null` when
 * `cID` is absent, empty, or invalid.
 */
export function useDecodedCardSharePayload(
    cardIdQuery: CardIdQuery,
): CardSharePayload | null {
    const payload = useMemo(() => {
        if (!cardIdQuery.isPresent) {
            return null
        }
        const raw = cardIdQuery.value.trim()
        if (!raw) {
            return null
        }
        return decodeCardSharePayload(raw)
    }, [cardIdQuery.isPresent, cardIdQuery.value])

    useEffect(() => {
        if (payload != null) {
            applyCardSharePayloadToStore(payload)
        }
    }, [payload])

    return payload
}
